if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.desktopPrevButton = this.elements.viewer?.querySelector(
          '.slider-button--prev',
        );
        this.desktopNextButton = this.elements.viewer?.querySelector(
          '.slider-button--next',
        );
        this.counterCurrent = this.elements.viewer?.querySelector(
          '.slider-counter--current',
        );
        this.counterTotal = this.elements.viewer?.querySelector(
          '.slider-counter--total',
        );

        if (this.dataset.autoplay === 'true') {
          this.startAutoplay();
          this.addEventListener('mouseenter', this.stopAutoplay.bind(this));
          this.addEventListener('mouseleave', this.startAutoplay.bind(this));
          this.addEventListener('focusin', this.stopAutoplay.bind(this));
          this.addEventListener('focusout', this.startAutoplay.bind(this));
        }

        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener(
          'slideChanged',
          debounce(this.onSlideChanged.bind(this), 500),
        );
        this.elements.thumbnails
          .querySelectorAll('[data-target]')
          .forEach((mediaToSwitch) => {
            mediaToSwitch
              .querySelector('button')
              .addEventListener(
                'click',
                this.setActiveMedia.bind(
                  this,
                  mediaToSwitch.dataset.target,
                  false,
                ),
              );
          });

        this.desktopPrevButton?.addEventListener(
          'click',
          this.onDesktopButtonClick.bind(this, -1),
          true,
        );
        this.desktopNextButton?.addEventListener(
          'click',
          this.onDesktopButtonClick.bind(this, 1),
          true,
        );

        if (
          this.dataset.desktopLayout.includes('thumbnail') &&
          this.mql.matches
        )
          this.removeListSemantic();

        this.updateCounter();
      }

      onSlideChanged(event) {
        this.elements.viewer
          ?.querySelectorAll('.product__media-item[data-media-id]')
          .forEach((element) => element.classList.remove('is-active'));
        event.detail.currentElement?.classList?.add('is-active');

        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`,
        );
        this.setActiveThumbnail(thumbnail);
      }

      connectedCallback() {
        if (this.dataset.autoplay === 'true') this.startAutoplay();
        this.updateCounter();
      }

      disconnectedCallback() {
        this.stopAutoplay();
      }

      setActiveMedia(mediaId, prepend) {
        const shouldSkipVerticalPageScroll = this.isAutoplayAdvancing;
        const activeMedia =
          this.elements.viewer.querySelector(
            `.product__media-item[data-media-id="${mediaId}"]`,
          ) ||
          this.elements.viewer.querySelector(
            '.product__media-item[data-media-id]',
          );
        if (!activeMedia) {
          return;
        }
        this.elements.viewer
          .querySelectorAll('.product__media-item[data-media-id]')
          .forEach((element) => {
            element.classList.remove('is-active');
          });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia &&
            activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(
              `[data-target="${mediaId}"]`,
            );
            activeThumbnail.parentElement.firstChild !== activeThumbnail &&
              activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.updateCounter();

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (this.isDesktopThumbnailLayout()) {
            return;
          }

          if (this.elements.viewer?.slider) {
            this.elements.viewer.slider.scrollTo({
              left: activeMedia.offsetLeft,
              behavior: 'smooth',
            });
          } else if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({
              left: activeMedia.offsetLeft,
            });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          if (shouldSkipVerticalPageScroll) return;
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${mediaId}"]`,
        );
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(
          activeMedia,
          activeThumbnail.dataset.mediaPosition,
        );
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);

        if (this.isThumbnailLayoutVertical()) {
          const slider = this.elements.thumbnails.slider;
          const visibleTop = slider.scrollTop;
          const visibleBottom = visibleTop + slider.clientHeight - 10;
          const thumbnailTop = thumbnail.offsetTop;
          const thumbnailBottom = thumbnailTop + thumbnail.clientHeight;

          if (thumbnailTop >= visibleTop && thumbnailBottom <= visibleBottom)
            return;

          slider.scrollTo({ top: Math.max(thumbnailTop - 10, 0) });
          return;
        }

        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({
          left: thumbnail.offsetLeft,
        });
      }

      isThumbnailLayoutVertical() {
        if (!this.elements.thumbnails?.slider) return false;

        return (
          window.getComputedStyle(this.elements.thumbnails.slider)
            .flexDirection === 'column'
        );
      }

      getMediaItems() {
        return Array.from(
          this.elements.viewer?.querySelectorAll(
            '.product__media-item[data-media-id]',
          ) || [],
        );
      }

      getCurrentMediaIndex() {
        const mediaItems = this.getMediaItems();
        return mediaItems.findIndex((item) =>
          item.classList.contains('is-active'),
        );
      }

      isDesktopThumbnailLayout() {
        return (
          this.mql.matches && this.dataset.desktopLayout?.includes('thumbnail')
        );
      }

      onDesktopButtonClick(direction, event) {
        if (!this.isDesktopThumbnailLayout()) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        this.showAdjacentMedia(direction);
      }

      showAdjacentMedia(direction) {
        const mediaItems = this.getMediaItems();
        if (mediaItems.length < 2) return;

        const currentIndex = this.getCurrentMediaIndex();
        const nextIndex =
          currentIndex >= 0
            ? (currentIndex + direction + mediaItems.length) % mediaItems.length
            : 0;
        this.setActiveMedia(mediaItems[nextIndex].dataset.mediaId, false);
      }

      updateCounter() {
        if (!this.counterCurrent || !this.counterTotal) return;

        const mediaItems = this.getMediaItems();
        if (!mediaItems.length) {
          this.counterCurrent.textContent = '0';
          this.counterTotal.textContent = '0';
          return;
        }

        const currentIndex = this.getCurrentMediaIndex();
        this.counterCurrent.textContent = String(
          currentIndex >= 0 ? currentIndex + 1 : 1,
        );
        this.counterTotal.textContent = String(mediaItems.length);
      }

      advanceSlide() {
        this.isAutoplayAdvancing = true;
        this.showAdjacentMedia(1);
        this.isAutoplayAdvancing = false;
      }

      startAutoplay() {
        this.stopAutoplay();
        if (this.dataset.autoplay !== 'true') return;

        const delaySeconds = Number(this.dataset.autoplaySpeed || 5);
        if (!Number.isFinite(delaySeconds) || delaySeconds <= 0) return;

        this.autoplayInterval = window.setInterval(() => {
          if (document.hidden) return;
          this.advanceSlide();
        }, delaySeconds * 1000);
      }

      stopAutoplay() {
        if (!this.autoplayInterval) return;
        window.clearInterval(this.autoplayInterval);
        this.autoplayInterval = null;
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector(
          '.product__modal-opener--image img',
        );
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML =
            window.accessibilityStrings.imageAvailable.replace(
              '[index]',
              position,
            );
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader =
          this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) =>
          slide.setAttribute('role', 'presentation'),
        );
      }
    },
  );
}
